import { Link, useLocation } from "@tanstack/react-router";
import { Home, Menu, ShoppingBag, Store } from "lucide-react";
import { useState } from "react";
import { cn } from "#/lib/utils";
import { ROUTES } from "../../lib/routes.js";
import { useCartCount } from "../../store/cart.js";
import { Badge } from "../ui/badge.js";
import { Sheet, SheetBody, SheetHeader, SheetTitle } from "../ui/sheet.js";

interface NavTab {
  label: string;
  to: string;
  icon: typeof Home;
  matchPrefix?: string;
}

const NAV_TABS: ReadonlyArray<NavTab> = [
  {
    label: "Home",
    to: ROUTES.home,
    icon: Home,
    matchPrefix: undefined,
  },
  {
    label: "Shop",
    to: ROUTES.shop,
    icon: Store,
    matchPrefix: "/shop",
  },
  {
    label: "Cart",
    to: ROUTES.cart,
    icon: ShoppingBag,
    matchPrefix: "/cart",
  },
];

const MENU_SECTIONS = [
  {
    title: "Browse",
    items: [
      { label: "Shop all", to: ROUTES.shop },
      { label: "Sale", to: ROUTES.sale },
      { label: "BJU Press", to: ROUTES.bju },
      { label: "LIFEPAC", to: ROUTES.lifepac },
      { label: "Living Books", to: ROUTES.livingBooks },
      { label: "Tyndale Kids", to: ROUTES.tyndale },
    ],
  },
  {
    title: "Help",
    items: [
      { label: "FAQs", to: ROUTES.faqs },
      { label: "Contact", to: ROUTES.contact },
      { label: "Track Order", to: ROUTES.track },
      { label: "Shipping", to: ROUTES.shipping },
      { label: "Returns", to: ROUTES.returns },
    ],
  },
  {
    title: "You",
    items: [
      { label: "My Account", to: ROUTES.account },
      { label: "About", to: ROUTES.about },
    ],
  },
] as const;

export interface MobileTabBarProps {
  onOpenCart: () => void;
  homeLogoSrc?: string;
}

export function MobileTabBar({ homeLogoSrc }: MobileTabBarProps) {
  const count = useCartCount();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const isActive = (tab: NavTab) =>
    tab.matchPrefix
      ? pathname === tab.to || pathname.startsWith(`${tab.matchPrefix}/`)
      : pathname === tab.to;

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 lg:hidden pointer-events-none"
        aria-label="Primary"
      >
        <div className="ml-auto mr-[60px] md:mr-auto flex max-w-[520px] items-center gap-2 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-auto">
          <div className="flex flex-1 items-center gap-0.5 rounded-full border border-border bg-card/95 py-1 px-1.5 shadow-[0_8px_30px_rgb(0_0_0_/_0.12)] backdrop-blur-xl">
            {NAV_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = isActive(tab);
              if (tab.to === ROUTES.home && homeLogoSrc) {
                return (
                  <Link
                    key={tab.to}
                    to={tab.to}
                    aria-current={active ? "page" : undefined}
                    aria-label={tab.label}
                    className={cn(
                      "flex flex-1 items-center justify-center rounded-full py-1.5 transition-colors",
                      active ? "bg-secondary" : "hover:bg-secondary/60",
                    )}
                  >
                    <img
                      src={homeLogoSrc}
                      alt=""
                      className="h-8 w-8 object-cover"
                    />
                  
                  </Link>
                );
              }
              if (tab.to === ROUTES.cart ) {
                 return (
                  <Link
                    key={tab.to}
                    to={tab.to}
                    aria-current={active ? "page" : undefined}
                    aria-label={tab.label}
                    className={cn(
                    "flex flex-1 flex-col items-center justify-center  relative  rounded-full py-1.5 px-2 gap-0.5 transition-colors",
                    active
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  >
                    <Icon className="size-5" />
                    <span className="text-[10px] font-medium leading-none">
                      {tab.label}
                    </span>
                    {count > 0 && (
                      <Badge
                          variant="default"
                          className={cn("absolute bg-primary-foreground text-primary border-0 p-2 py-1 -mr-1 right-2 top-2 min-w-[1.25rem] justify-center", active ? "bg-primary-foreground text-primary" : "text-white bg-secondary-foreground" )}
                        >
                          {count}
                      </Badge>
                      )}
                  </Link>

                );
              }

              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-full py-1.5 transition-colors",
                    active
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className="size-5" />
                  <span className="text-[10px] font-medium leading-none">
                    {tab.label}
                  </span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 rounded-full border-0 bg-transparent py-1.5 text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <Menu className="size-5" />
              <span className="text-[10px] font-medium leading-none">Menu</span>
            </button>
          </div>
          {/* <button
            type="button"
            onClick={onOpenCart}
            aria-label={count > 0 ? `Cart, ${count} items` : "Cart"}
            className="sm relative inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-primary-foreground border-0 shadow-[0_10px_30px_rgb(0_0_0_/_0.18)] hover:brightness-105 active:scale-[0.98] transition-[transform,filter] cursor-pointer"
          >
            <ShoppingBag className="size-5" />
            {count > 0 && (
              <Badge
                variant="default"
                className="bg-primary-foreground text-primary border-0 px-1.5 min-w-[1.25rem] justify-center"
              >
                {count}
              </Badge>
            )}
          </button> */}
        </div>
      </nav>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen} side="bottom">
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <SheetBody className="pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col gap-6">
            {MENU_SECTIONS.map((section) => (
              <div key={section.title} className="flex flex-col gap-1">
                <div className="px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </div>
                <div className="flex flex-col">
                  {section.items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      className={cn(
                        "px-3 py-3 rounded-xl text-base text-foreground hover:bg-muted",
                        pathname === item.to && "bg-secondary font-semibold",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </SheetBody>
      </Sheet>
    </>
  );
}
